import {Body1, Button, Card, makeStyles, MessageBar, Subtitle1, Title3, tokens} from "@fluentui/react-components";
import React, {useEffect} from "react";
import {useSetAtom} from "jotai";
import {processPlotAtom, selectedTabAtom} from "../../store";
import {useApiQuery} from "../../services/useApiQuery.ts";
import {Access} from "../../services/types.ts";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        padding: '20px',
        height: '200%',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    stepsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    stepBox: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '16px',
        borderRadius: '8px',
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        transition: 'all 0.2s ease',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
            boxShadow: tokens.shadow4,
        },
        '@media (max-width: 640px)': {
            flexDirection: 'column',
        },
    },
    stepNumber: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: '40px',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        fontSize: '20px',
        fontWeight: 'bold',
        marginRight: '16px',
        flexShrink: 0,
    },
    stepContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flex: 1,
    },
    stepAction: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingTop: '4px',
        minWidth: '150px',
        '@media (max-width: 640px)': {
            width: '100%',
            marginTop: '16px',
            paddingTop: 0,
            minWidth: 'auto',
            '& button': {
                width: '100%',
            },
        },
    },
    intro: {
        marginBottom: '16px',
    },
    messageBar: {
        margin: "10px",
    },
    stepRow: {
        display: 'flex',
        width: '100%',
        alignItems: 'flex-start',
    },
    stepHeader: {
        display: 'flex',
        flex: 1,
        alignItems: 'flex-start',
        '@media (max-width: 640px)': {
            width: '100%',
        },
    }
});

export const HomeTab: React.FC = () => {
    const styles = useStyles();
    const { t } = useTranslation();
    const setSelectedTab = useSetAtom(selectedTabAtom);
    const setProcessPlotAtom = useSetAtom(processPlotAtom)
    const {
        data: access,
        isLoading: accessLoading,
    } = useApiQuery<Access>('/api/access/check');
    const accessGranted = access?.access === true;

    useEffect(() => {
        if (access?.processplot) {
            setProcessPlotAtom(access?.processplot)
        }
    }, [access]);

    const handleNavigate = (tabName: "groups" | "availableModules" | "activeModules") => {
        setSelectedTab(tabName);
    };

    return (
        <Card>
            <div className={styles.container}>
                <div className={styles.header}>
                    <Title3>{t("nav.home")}</Title3>
                </div>
                <MessageBar intent="info">
                    <Body1 className={styles.messageBar}><span dangerouslySetInnerHTML={{__html:t("message.early-access")}}></span></Body1>
                </MessageBar>
                <div className={styles.stepsContainer}>

                    <div className={styles.stepBox}>
                        <div className={styles.stepHeader}>
                            <div className={styles.stepNumber}>1</div>
                            <div className={styles.stepContent}>
                                <Subtitle1 style={{fontWeight: 'bold'}}>{t("button.authorize")}</Subtitle1>
                                <Body1>
                                    {t("text.enter-product-key")}
                                </Body1>
                                <Body1 style={{color: tokens.colorPalettePumpkinForeground2}}>
                                </Body1>
                            </div>
                        </div>
                        <div className={styles.stepAction}>
                            <Button
                                appearance="primary"
                                disabled={accessGranted || accessLoading}
                                onClick={() => handleNavigate("groups")}
                            >
                                {accessLoading ? t("text.checking-credentials") : accessGranted ? t("button.key-validated") : t("button.set-key")}
                            </Button>
                        </div>
                    </div>
                    {/* Step 1 */}
                    <div className={styles.stepBox}>
                        <div className={styles.stepHeader}>
                            <div className={styles.stepNumber}>2</div>
                            <div className={styles.stepContent}>
                                <Subtitle1 style={{fontWeight: 'bold'}}>{t("text.setup-audience")}</Subtitle1>
                                <Body1>
                                    {t("text.setup-audience-description")}
                                </Body1>
                            </div>
                        </div>
                        <div className={styles.stepAction}>
                            <Button
                                appearance="primary"
                                onClick={() => handleNavigate("groups")}
                            >
                                {t("button.create-audience")}
                            </Button>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className={styles.stepBox}>
                        <div className={styles.stepHeader}>
                            <div className={styles.stepNumber}>3</div>
                            <div className={styles.stepContent}>
                                <Subtitle1 style={{fontWeight: 'bold'}}>{t("text.select-message-series")}</Subtitle1>
                                <Body1>
                                    {t("text.select-message-series-description")}
                                </Body1>
                            </div>
                        </div>
                        <div className={styles.stepAction}>
                            <Button
                                appearance="primary"
                                onClick={() => handleNavigate("availableModules")}
                            >
                                {t("button.browse-message-series")}
                            </Button>
                        </div>
                    </div>

                    {/* Step 3 */}
                    <div className={styles.stepBox}>
                        <div className={styles.stepHeader}>
                            <div className={styles.stepNumber}>4</div>
                            <div className={styles.stepContent}>
                                <Subtitle1 style={{fontWeight: 'bold'}}>{t("text.launch-campaign")}</Subtitle1>
                                <Body1>
                                    {t("text.launch-campaign-description")}
                                </Body1>
                            </div>
                        </div>
                        <div className={styles.stepAction}>
                            <Button
                                appearance="primary"
                                onClick={() => handleNavigate("activeModules")}
                            >
                                {t("button.start-campaign")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};
